import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Edit, Trash2, UserX, Calendar, Phone, MapPin, User, IndianRupee } from 'lucide-react';
import { Worker } from '../../types';

interface WorkerCardProps {
  worker: Worker;
  onUpdate: (workerId: string, updates: Partial<Worker>) => void;
  onDeactivate: (workerId: string) => void;
  onDelete: (workerId: string) => void;
}

const WorkerCard: React.FC<WorkerCardProps> = ({
  worker,
  onUpdate,
  onDeactivate,
  onDelete
}) => {
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      case 'on_leave':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'inactive':
        return 'Inactive';
      case 'on_leave':
        return 'On Leave';
      default:
        return status;
    }
  };

  const getGenderText = (gender: string) => {
    switch (gender) {
      case 'male':
        return 'Male';
      case 'female':
        return 'Female';
      case 'other':
        return 'Other';
      default:
        return gender;
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN');
  };

  const calculateTenure = (onboardingDate: any) => {
    if (!onboardingDate) return 'N/A';
    const startDate = onboardingDate.toDate ? onboardingDate.toDate() : new Date(onboardingDate);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) {
      return `${diffDays} days`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months > 1 ? 's' : ''}`;
    } else {
      const years = Math.floor(diffDays / 365);
      return `${years} year${years > 1 ? 's' : ''}`;
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    setIsUpdatingStatus(true);
    try {
      await onUpdate(worker.id, { availabilityStatus: newStatus as any });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  return (
    <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-200 hover:shadow-md transition-shadow duration-200">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <User className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-gray-900">{worker.fullName}</h3>
              <p className="text-sm text-gray-500">{getGenderText(worker.gender)}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Link
              to={`/workers/edit/${worker.id}`}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <Edit className="h-4 w-4" />
            </Link>
            {worker.isActive ? (
              <button
                onClick={() => onDeactivate(worker.id)}
                className="text-gray-400 hover:text-yellow-600 transition-colors duration-200"
                title="Deactivate Worker"
              >
                <UserX className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={() => onDelete(worker.id)}
                className="text-gray-400 hover:text-red-600 transition-colors duration-200"
                title="Delete Worker"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Contact Info */}
        <div className="space-y-3 mb-4">
          <div className="flex items-center text-sm text-gray-600">
            <Phone className="h-4 w-4 mr-2 text-gray-400" />
            <span>{worker.phoneNumber}</span>
          </div>
          <div className="flex items-start text-sm text-gray-600">
            <MapPin className="h-4 w-4 mr-2 mt-0.5 text-gray-400 flex-shrink-0" />
            <span className="line-clamp-2">{worker.address}</span>
          </div>
        </div>

        {/* Status and Tenure */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Calendar className="h-4 w-4 mr-2 text-gray-400" />
            <span className="text-sm text-gray-600">
              Joined: {formatDate(worker.onboardingDate)}
            </span>
          </div>
          <span className="text-sm text-gray-500">
            {calculateTenure(worker.onboardingDate)}
          </span>
        </div>

        {/* Status Badge */}
        <div className="flex items-center justify-between mb-4">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(worker.availabilityStatus)}`}>
            {getStatusText(worker.availabilityStatus)}
          </span>
          {worker.salary && (
            <div className="flex items-center text-sm text-gray-600">
              <IndianRupee className="h-4 w-4 mr-1" />
              <span>{worker.salary.toLocaleString('en-IN')}/week</span>
            </div>
          )}
        </div>

        {/* Quick Status Update */}
        {worker.isActive && (
          <div className="border-t border-gray-200 pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quick Status Update
            </label>
            <select
              value={worker.availabilityStatus}
              onChange={(e) => handleStatusChange(e.target.value)}
              disabled={isUpdatingStatus}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm disabled:opacity-50"
            >
              <option value="active">Active</option>
              <option value="on_leave">On Leave</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        )}

        {/* View Details Link */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <Link
            to={`/workers/${worker.id}`}
            className="text-sm text-green-600 hover:text-green-700 font-medium"
          >
            View Details →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default WorkerCard; 